import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO, { SITE_URL } from '@/components/base/SEO';

export default function TemplatesGuide() {
  return (
    <main>
      <SEO
        title="Best Litbuy Spreadsheet Templates for E-Commerce Sellers | LitbuySpreadsheets.online"
        description="Discover the most powerful pre-built Litbuy Spreadsheet templates: inventory tracker, sales analytics, profit calculator, and more. Save hours of setup time."
        canonicalPath="/litbuy-spreadsheet-templates"
        keywords="litbuy spreadsheet templates, inventory tracker template, sales analytics template, profit margin calculator, e-commerce spreadsheet templates"
        schemas={[
          {
            '@type': 'Article',
            headline: 'Best Litbuy Spreadsheet Templates for E-Commerce Sellers',
            description: 'Discover the most powerful pre-built templates that save hours of setup time: inventory tracker, sales analytics, profit margin calculator, and more.',
            author: { '@type': 'Organization', name: 'Litbuy Editorial Team' },
            publisher: { '@type': 'Organization', name: 'Litbuy Spreadsheets', url: SITE_URL },
            datePublished: '2026-06-15',
            dateModified: '2026-06-20',
            mainEntityOfPage: { '@type': 'WebPage', '@id': `${SITE_URL}/litbuy-spreadsheet-templates` },
          },
          {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Best Litbuy Spreadsheet Templates', item: `${SITE_URL}/litbuy-spreadsheet-templates` },
            ],
          },
        ]}
      />
      <Navbar />
      <article>
        <section className="relative pt-24 md:pt-28 pb-16 md:pb-20 px-4 md:px-6 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700 font-medium">Best Litbuy Spreadsheet Templates</span>
            </nav>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Templates</span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight">
              Best Litbuy Spreadsheet Templates for E-Commerce Sellers
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
              Discover the most powerful pre-built templates that save hours of setup time: inventory tracker, sales analytics, profit margin calculator, and more.
            </p>
          </div>
        </section>

        <section className="relative py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto space-y-14">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">Top Templates Every Seller Needs</h2>
              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">Litbuy Spreadsheet comes with an extensive library of templates designed for specific e-commerce workflows. These are not generic spreadsheets, they are purpose-built tools that match how online sellers actually work.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {[
                  { title: 'Inventory Tracker', desc: 'Auto-calculates total stock value, flags low inventory, and generates restock lists. Supports multi-warehouse tracking.' },
                  { title: 'Sales Analytics', desc: 'Daily, weekly, monthly breakdowns by product, category, and channel. Visual charts included.' },
                  { title: 'Profit Calculator', desc: 'Factors cost of goods, shipping, platform fees, and returns to show true profit per item.' },
                  { title: 'Product Comparison', desc: 'Side-by-side performance comparison to identify winners and underperformers in your catalog.' },
                ].map((tpl) => (
                  <div key={tpl.title} className="bg-background-100 rounded-xl p-5">
                    <h4 className="font-heading text-base font-semibold text-foreground-900 mb-2">{tpl.title}</h4>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{tpl.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-background-100 rounded-2xl p-6 md:p-10 text-center">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">Get These Templates</h2>
              <p className="text-sm text-foreground-600 mb-6 max-w-lg mx-auto">All templates are available on the official Litbuy Spreadsheet store.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="https://kaspreadsheet.com/" target="_blank" rel="noopener nofollow" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-primary-500 text-white font-semibold text-sm hover:bg-primary-600 transition-colors">Visit Main Store <i className="ri-arrow-right-up-line"></i></a>
                <Link to="/" className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-primary-300 text-primary-600 font-semibold text-sm hover:bg-primary-50 transition-colors">Back to Home <i className="ri-home-4-line"></i></Link>
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-8">
              <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">Continue Reading</h3>
              <div className="flex flex-wrap gap-2">
                <Link to="/litbuy-spreadsheet-guide" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Complete Guide</Link>
                <Link to="/" className="whitespace-nowrap cursor-pointer px-4 py-2 rounded-full bg-background-100 text-sm text-foreground-600 hover:text-primary-500 hover:bg-primary-50 transition-colors">Home</Link>
              </div>
            </div>
          </div>
        </section>
      </article>
      <Footer />
    </main>
  );
}